import { ToggleButton as ToggleButtonMaterial, ToggleButtonGroup as ToggleButtonGroupMaterial } from "@mui/material"
import React, { MouseEvent } from "react"
import styled, { css } from "styled-components"
import { theme } from "../../../theme"
import { ChipToggleFilterOption } from "./ChipToggleFilterOption"

export type ChipToggleFilterProps<TValue> = {
  options: ChipToggleFilterOption<TValue>[]
  onChange: (value: TValue | null) => void
  disabled?: boolean
  value: TValue | null
  defaultValue: TValue
}

export const ChipToggleFilter = <TValue extends string | number>(props: ChipToggleFilterProps<TValue>) => {
  const { options, value, onChange, disabled, defaultValue } = props

  const handleOnChange = (_: MouseEvent<HTMLElement>, newValue: TValue) => {
    if (!newValue) {
      onChange(defaultValue)
    } else {
      onChange(newValue)
    }
  }

  return (
    <ToggleChipGroup exclusive onChange={handleOnChange} value={value} disabled={disabled}>
      {!!options &&
        options.map((option) => (
          <ToggleChipButton key={option.value} value={option.value} disableRipple>
            <ChipLabel $active={option.value === value} $disabled={disabled}>
              {option.label}
            </ChipLabel>
          </ToggleChipButton>
        ))}
    </ToggleChipGroup>
  )
}

const DisabledStyle = css`
  background-color: ${theme.color.white2};
  color: ${theme.color.grey2};
`

const getNormalStyle = (active: boolean) => css`
  color: ${active ? theme.color.white : theme.color.black6};
  background-color: ${active ? theme.color.black4 : theme.color.grey5};
`

const ToggleChipGroup = styled(ToggleButtonGroupMaterial)`
  background-color: ${theme.color.grey5};
  border-radius: 34px;
  overflow: hidden;
`

const ToggleChipButton = styled(ToggleButtonMaterial)`
  text-transform: none;
  font-family: ${theme.font.regular};
  padding: 0;
  border: 0;

  &:hover {
    background-color: transparent;
  }

  &.Mui-selected {
    background-color: transparent;
    :hover {
      background-color: transparent;
    }
  }

  &.Mui-disabled {
    border: none;
    ${DisabledStyle}
  }
`

const ChipLabel = styled.div<{ $active: boolean; $disabled?: boolean }>`
  padding: 4px 12px;
  border-radius: 34px;
  transition: background-color 0.3s ease;

  ${(p) => (p.$disabled ? DisabledStyle : getNormalStyle(p.$active))}

  &:hover {
    background-color: ${(p) => (p.$active ? theme.color.grey2 : theme.color.white2)};
  }
`
